import {AutoLayoutJobMetadata, type AutoLayoutJob} from "@buildwithflux/models";
import {type SubscribedTypeCallback, SubscriptionManager} from "@buildwithflux/repositories";
import {isDevEnv, Logger, type Unsubscriber} from "@buildwithflux/shared";

import type {AutoLayoutEventBus, AutoLayoutEventData, AutoLayoutEventType} from "./AutoLayoutEventBus";
import type {DeepPcbBoardExportMetadata} from "./DeepPcbAutoLayoutBoardExporter";

export type DeepPcbAutoLayoutEventMap = {
    // Job lifecycle events - API requests
    "job.starting": {projectUid: string; layoutNodeUid: string; organizationUid?: string};
    "job.created": {jobUid: string | undefined; uploadUrl: string};
    "job.populate.completed": {jobUid: string | undefined};
    "job.submit.requested": {jobUid: string | undefined};
    "job.confirm.requested": {jobUid: string | undefined};
    "job.pause.requested": {jobUid: string | undefined};
    "job.resume.requested": {jobUid: string | undefined};
    "job.cancel.requested": {jobUid: string | undefined};
    "job.apply.requested": {jobUid: string | undefined};

    // Job lifecycle events - task starts
    "job.loading": {layoutNodeUid: string};
    "job.pausing": {};
    "job.resuming": {};
    "job.cancelling": {};

    // Job lifecycle events - actual state changes
    "job.loaded": {jobUid: string | undefined; backendJob: AutoLayoutJob};
    "job.paused": {jobUid: string | undefined};
    "job.resumed": {jobUid: string | undefined};
    "job.canceled": {jobUid: string | undefined};
    "job.applied": {jobUid: string | undefined};
    "job.failed": {jobUid: string | undefined; error: unknown};

    // Job state synchronization events
    "job.synced": {backendJob: AutoLayoutJob};
    "job.cleanedUp": {jobUid: string | undefined};

    // Iteration events
    "iteration.received": {jobUid: string | undefined; iterationCount: number};
    "iteration.processed": {jobUid: string | undefined; iterationIndex: number};
    "iteration.selecting": {iterationIndex: number};
    "iteration.selected": {
        jobUid: string | undefined;
        iterationIndex: number;
        iterationHash: string;
    };
    "iteration.applying": {};
    "iteration.baked": {jobUid: string | undefined};

    // Credits and metadata
    "credits.updated": {jobUid: string | undefined; creditsUsed: number};
    "metadata.updated": {jobUid: string | undefined; metadata: AutoLayoutJobMetadata};

    // Error handling
    "error.occurred": {error: unknown; message: string};

    // Net classification events
    "netClassification.started": {projectUid: string};
    "netClassification.completed": {projectUid: string; netCount: number; classificationsApplied: number};
    "netClassification.failed": {projectUid: string; error: unknown};

    // Review events
    "review.evaluation.started": {jobUid: string | undefined};
    "review.evaluation.completed": {jobUid: string | undefined; reviewUid: string};
    "review.evaluation.failed": {jobUid: string | undefined; error: unknown};

    // Board export events
    "board.export.started": {layoutNodeUid: string};
    "board.export.completed": {layoutNodeUid: string; metadata: DeepPcbBoardExportMetadata};
    "board.export.failed": {layoutNodeUid: string; error: unknown};

    // Service lifecycle events
    "service.shutting_down": {};
};

export type DeepPcbAutoLayoutEventType = AutoLayoutEventType<DeepPcbAutoLayoutEventMap>;

export type DeepPcbAutoLayoutEventData<T extends DeepPcbAutoLayoutEventType> = AutoLayoutEventData<
    DeepPcbAutoLayoutEventMap,
    T
>;

/**
 * DeepPCB implementation of the auto-layout event bus.
 * Uses the existing SubscriptionManager for proven reliability.
 */
export class DeepPcbAutoLayoutEventBus implements AutoLayoutEventBus<DeepPcbAutoLayoutEventMap> {
    private readonly subscriptionManager = new SubscriptionManager<
        DeepPcbAutoLayoutEventType,
        DeepPcbAutoLayoutEventData<DeepPcbAutoLayoutEventType>
    >({
        sendLatestNotifiedValueOnSubscribe: false,
    });

    constructor(private readonly logger: Logger) {}

    public emit<T extends DeepPcbAutoLayoutEventType>(
        event: T,
        data: DeepPcbAutoLayoutEventData<T>,
        sourceService?: string,
    ): void {
        if (isDevEnv()) {
            this.logger.debug(`[AutoLayoutEvent] [${sourceService}]`, {
                event,
                data,
            });
        }

        this.subscriptionManager.notify(event, data);
    }

    public subscribe<T extends DeepPcbAutoLayoutEventType>(
        event: T,
        handler: (data: DeepPcbAutoLayoutEventData<T>) => void,
    ): Unsubscriber {
        return this.subscriptionManager.addSubscription(
            event,
            handler as SubscribedTypeCallback<DeepPcbAutoLayoutEventData<DeepPcbAutoLayoutEventType>>,
        );
    }

    public subscribeOnce<T extends DeepPcbAutoLayoutEventType>(
        event: T,
        handler: (data: DeepPcbAutoLayoutEventData<T>) => void,
    ): Unsubscriber {
        const wrappedHandler = (data: DeepPcbAutoLayoutEventData<T>) => {
            handler(data);
            unsubscriber?.();
        };

        const unsubscriber = this.subscribe(event, wrappedHandler);

        return unsubscriber;
    }

    public subscribeMany(
        events: DeepPcbAutoLayoutEventType[],
        handler: (
            event: DeepPcbAutoLayoutEventType,
            data: DeepPcbAutoLayoutEventData<DeepPcbAutoLayoutEventType>,
        ) => void,
    ): Unsubscriber {
        const unsubscribers = events.map((event) =>
            this.subscribe(event, (data) => {
                handler(event, data);
            }),
        );

        return () => {
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            unsubscribers.forEach((unsub) => unsub());
        };
    }
}
